// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ShipmentPackageDto, ShipmentPackageLineDto } from "@insite/client-framework/Types/ApiModels";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import constants from "@store/constants";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackFileDownload from "@store/Framework/GoogleAnalytics/hooks/useTrackFileDownload";
import useTrackLink from "@store/Framework/GoogleAnalytics/hooks/useTrackLink";
import { getInvoicePdf, getShippingDocuments } from "@store/Framework/Services/documentService";
import Loading from "@store/Library/Components/Icons/Loading";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { useContext, useState } from "react";
import { css } from "styled-components";

export interface ProductLineOrderInformationProps {
    showInvoiceStatus?: boolean;
    openInvoicesInNewTab?: boolean;
    shipmentsDisplay?: Shipment[];
}

export type Shipment = ShipmentPackageLineDto & Partial<ShipmentPackageDto>;

export interface ProductLineOrderInformationStyles {
    wrapper?: InjectableCss;
    shipmentWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    deliveryDateValue?: TypographyPresentationProps;
    infoWrapper?: InjectableCss;
    downloadLink?: LinkPresentationProps;
    loadingDownloadLink?: LinkPresentationProps;
    statusLink?: LinkPresentationProps;
}

export const productLineOrderInformationStyles: ProductLineOrderInformationStyles = {
    shipmentWrapper: {
        css: css`
            display: flex;
            align-items: stretch;
            justify-content: space-between;
            gap: ${pxToRem(24)};
            flex-wrap: wrap;
        `,
    },
    label: orderV2Props.infoLabel,
    infoWrapper: mergeProps(orderV2Props.infoWrapper, {
        css: css`
            align-items: stretch;
            justify-content: space-between;

            &:last-child {
                justify-content: flex-end;
            }
        `,
    }),
    downloadLink: linkGetActionLinkProps(Download),
    loadingDownloadLink: linkGetActionLinkProps(Loading),
    statusLink: linkGetActionLinkProps(ChevronRight, "right"),
};

const styles = productLineOrderInformationStyles;

const ProductLineOrderInformation = ({
    shipmentsDisplay,
    showInvoiceStatus,
    openInvoicesInNewTab,
}: ProductLineOrderInformationProps) => {
    const [invoiceIsLoading, setInvoiceIsLoading] = useState(false);
    const [shippingDocumentsIsLoading, setShippingDocumentsIsLoading] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const trackFileDownload = useTrackFileDownload();
    const trackShippingLink = useTrackLink();
    const invoiceHistoryLink = usePageLinkByPageType(constants.pageType.invoiceHistory);
    const handleOpenInvoice = (invoiceNumber: string) => {
        getDocumentsWrapper(
            getInvoicePdf,
            { invoiceNumber, isShipment: true },
            { stateSetter: setInvoiceIsLoading, toasterContext },
        );
        trackFileDownload({ file_category: "Invoice" });
    };

    const handleDownloadShippingDocuments = (shipmentNumber: string) => {
        getDocumentsWrapper(
            getShippingDocuments,
            { shipmentNumber },
            { stateSetter: setShippingDocumentsIsLoading, toasterContext },
        );
        trackFileDownload({ file_category: "Shipping Document" });
    };

    const handleTrackDocument = () => {
        trackShippingLink({ link_clicked: "Order Details - ETA details" });
    };

    if (!shipmentsDisplay || shipmentsDisplay.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="order_line_shipments">
            {shipmentsDisplay.map(shipment => {
                if (!shipment) {
                    return null;
                }

                return (
                    <StyledWrapper {...styles.shipmentWrapper} key={shipment.id}>
                        {shipment.eta && (
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.label}>{translate("Delivery Date")}</Typography>
                                {shipment.mapUrl ? (
                                    <Link
                                        href={shipment.mapUrl}
                                        disabled={!shipment.mapUrl}
                                        target="_blank"
                                        onClick={handleTrackDocument}
                                    >
                                        {shipment.eta}
                                    </Link>
                                ) : (
                                    <Typography {...styles.deliveryDateValue}>{shipment.eta}</Typography>
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.infoWrapper}>
                            <Typography {...styles.label}>{translate("Shipping Documents")}</Typography>
                            <Link
                                {...(shippingDocumentsIsLoading ? styles.loadingDownloadLink : styles.downloadLink)}
                                disabled={
                                    !shipment.shipmentNumber ||
                                    shippingDocumentsIsLoading ||
                                    !shipment.canDownloadShipmentDocument
                                }
                                onClick={() => handleDownloadShippingDocuments(shipment.shipmentNumber)}
                            >
                                {shipment.shipmentNumberDisplay}
                            </Link>
                        </StyledWrapper>
                        {shipment.invoiceNumber && (
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.label}>{translate("Invoice")}</Typography>
                                <Link
                                    {...(invoiceIsLoading ? styles.loadingDownloadLink : styles.downloadLink)}
                                    onClick={() => handleOpenInvoice(shipment.invoiceNumber)}
                                    disabled={!shipment.invoiceNumber || invoiceIsLoading}
                                >
                                    {shipment.invoiceNumberDisplay}
                                </Link>
                            </StyledWrapper>
                        )}
                        {showInvoiceStatus && (
                            <StyledWrapper {...styles.infoWrapper}>
                                {shipment.invoiceNumberDisplay && invoiceHistoryLink?.url && (
                                    <Link
                                        {...styles.statusLink}
                                        href={`${invoiceHistoryLink?.url}?invoiceNumber=${shipment.invoiceNumberDisplay}`}
                                        target={openInvoicesInNewTab ? "_blank" : ""}
                                    >
                                        {translate("See Invoice Status")}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

export default ProductLineOrderInformation;
